import { NgStyle } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { ResponsiveService } from '@shared/services/responsive';
import { getUuid } from '@shared/utils';
import { debounceTime, merge, skip, Subject, take } from 'rxjs';
import { Image, ImageSize, MAX_FRAME_RATIO_GAP, MAX_PIXEL_DENSITY, PregenerationSize, Size } from './models';
import { getCDNLink } from './utils';

@Component({
  selector: 'app-image',
  templateUrl: './image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle],
})
export class ImageComponent implements OnInit, OnDestroy {
  /**
   * The main image input (required).
   * Contains basic image information such as title, link, alt, etc.
   */
  readonly image = input.required<Image>();

  /**
   * Defines the configured size (width/height) for both mobile and desktop.
   * If not provided, the component will try to fill the available space.
   */
  readonly size = input<ImageSize>();

  /**
   * Frame this image is laid out in at its widest, for the SERVER PASS ONLY. Unlike `size`,
   * it never reaches the layout: it only picks the size asked of the CDN.
   *
   * `width` alone suits a full-width visual whose height follows the viewport, the height
   * being then deduced from the file's own ratio. Declaring both describes the frame whole,
   * and the CDN crops to it exactly as the client would.
   */
  readonly pregenerationSize = input<PregenerationSize>();

  /**
   * Represents additional CSS classes to be applied to the image element.
   * Can be used to customize the image appearance through dynamic class bindings.
   */
  readonly customClass = input('');

  /**
   * If true, preserves the aspect ratio when resizing the image.
   * When false, the component can adjust dimensions to fill available space.
   */
  readonly keepAspectRatio = input(false);

  /**
   * Enables specific loading optimizations if true (e.g., fetchpriority="high").
   * Useful for the Largest Contentful Paint (LCP) image.
   */
  readonly isPriority = input(false);

  /**
   * If true, applies a smooth scale-up effect on hover within the container.
   */
  readonly hoverEffect = input(false);

  /** Represents the loading state (true = loading, false = fully loaded). */
  readonly isLoading = model(true);
  /** A reference to the DOM element wrapping the image. */
  readonly wrapperImageElement = viewChild<ElementRef<HTMLElement>>('wrapperImage');
  /** Reactive signal for the computed width of the component container. */
  readonly width = signal<number>(0);
  /** Reactive signal for the computed height of the component container. */
  readonly height = signal<number>(0);
  /**
   * Indicates whether the image should use a "fill" mode,
   * typically applying object-cover if no explicit dimensions are set.
   */
  readonly isFullWidth = signal(false);
  /**
   * Holds the array of images, usually a single-entry array
   * with a computed CDN link that takes into account the current dimensions.
   */
  readonly imagesArray = signal<Image[] | null>(null);
  /** Represents the error state (true = error, false = no error). */
  readonly isError = signal(false);
  /**
   * Pixel size of the file currently rendered. Kept so a second request can be
   * skipped when what is already on screen covers the container.
   */
  private readonly renderedSize = signal<Size | null>(null);
  /** Service to handle responsive layout changes and breakpoints */
  private readonly responsiveService = inject(ResponsiveService);
  /** Current breakpoint (e.g., 'mobile', 'desktop') derived from ResponsiveService. */
  readonly breakpoints = this.responsiveService.breakpoints;
  /** A boolean variable indicating whether the current environment is a server.*/
  private readonly isServer = inject(PlatformContextService).isServer();
  private readonly logs = inject(LogsService);
  /**
   * Internal ResizeObserver to watch for container size changes
   * and update width/height signals accordingly.
   */
  private resizeObserver: ResizeObserver | null = null;

  /** Subject used to emit resize events so they can be processed. */
  private readonly resizeSubject = new Subject<{ width: number; height: number }>();

  /** Stores the current container dimensions for width and height calculations */
  private containerSize: Size = null!;

  constructor() {
    // Schedule the ResizeObserver initialization after the next render cycle.
    afterNextRender(() => {
      this.adoptServerRenderedImage();
      this.initResizeObserver();
    });

    /**
     * Observe the first resize event immediately (take(1)),
     * then observe subsequent events with a 200ms debounce to avoid frequent updates.
     */
    const firstEvent$ = this.resizeSubject.pipe(take(1));
    const subsequentEvents$ = this.resizeSubject.pipe(skip(1), debounceTime(200));

    merge(firstEvent$, subsequentEvents$)
      .pipe(takeUntilDestroyed())
      .subscribe(({ width, height }) => {
        this.containerSize = { width, height };
        // Update internal dimensions
        this.updateDimensions(width, height);

        // Generate a new CDN link based on updated dimensions
        const requestSize = this.getRequestSize();
        const cdnLink = getCDNLink(this.image(), requestSize, this.logs);

        // Check whether we need to update the image array with the new link
        if (this.shouldUpdateImage(cdnLink)) {
          this.isLoading.set(true);

          // Refresh the image entry with the newly built CDN link
          const newImage = {
            ...this.image(),
            cdnLink,
            randomId: getUuid(),
          };
          this.renderedSize.set(requestSize);
          this.imagesArray.set([newImage]);
        }
      });

    effect(() => {
      const currentImage = this.image();
      const containerWidth = this.containerSize?.width;
      const containerHeight = this.containerSize?.height;
      let width = this.width();
      let height = this.height();
      if (containerWidth || containerHeight) {
        this.updateDimensions(containerWidth, containerHeight);
      }

      if (!currentImage || width <= 0 || height <= 0) {
        return;
      }

      const requestSize = this.getRequestSize();
      const cdnLink = getCDNLink(currentImage, requestSize, this.logs);
      if (this.shouldUpdateImage(cdnLink)) {
        this.isLoading.set(true);
        this.isError.set(false);

        const newImage = {
          ...currentImage,
          cdnLink,
          randomId: getUuid(),
        };
        this.renderedSize.set(requestSize);
        this.imagesArray.set([newImage]);
      }
    });
  }

  /**
   * Angular lifecycle hook that is called after component initialization.
   * Calculates and sets the width and height properties based on the image sizes.
   * Generates a new CDN link for the image with the updated dimensions and updates the `imagesArray`.
   */
  ngOnInit(): void {
    // This part is used for SSG (Static Site Generation) to avoid CLS (Cumulative Layout Shift).
    if (this.isServer) {
      this.renderPregeneratedImage();
    }
  }

  /**
   * Lifecycle hook that performs cleanup.
   * Disconnects the ResizeObserver and completes the Subject.
   */
  ngOnDestroy(): void {
    // Stop observing size changes
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.resizeSubject.complete();
  }

  /**
   * Manually set the loading state to false upon image load completion.
   * This can be used in the template's load or error event handlers.
   */
  setLoadingFalse(): void {
    this.isLoading.set(false);
  }

  onError(): void {
    this.isError.set(true);
    this.isLoading.set(false);
  }

  /**
   * Builds the image entry at the pregeneration size, the one the layout is not measured for yet.
   */
  private renderPregeneratedImage(): void {
    const { width, height } = this.getSizes(this.size()?.mw ?? -1, this.size()?.mh ?? -1);

    // If the computed width is -1, enable full-width mode
    if (width === -1) {
      this.setFullWidthMode();
    }

    // Layout keeps the dimensions it always had. Feeding the pregeneration size into these
    // signals would size the inner box to the image instead of to its container, which
    // overflows the card and pushes its overlays out of view.
    this.width.set(width);
    this.height.set(height);

    const pregenerationSize = this.getPregenerationSize(width, height);
    this.renderedSize.set(pregenerationSize);

    const cdnLink = getCDNLink(this.image(), pregenerationSize, this.logs);

    const newImage = {
      ...this.image(),
      cdnLink,
      randomId: getUuid(),
    };

    this.imagesArray.set([newImage]);
  }

  /**
   * Dimensions asked of the CDN during the server pass, which never reach the layout.
   *
   * Without any configured size the URL would carry no resize parameter at all, and
   * Cloudflare then serves the full-size original (measured: 1727 Ko on average
   * instead of 78 Ko, up to 14 Mo on a single homepage image).
   */
  private getPregenerationSize(width: number, height: number): Size {
    // A block that knows its frame declares it whole, and the CDN crops to it exactly as
    // the client would. Measured across 1034 slots: a frame of 128x128 was being served
    // 1200px wide, and a 2:1 card paid for twice the height it shows.
    const frame = this.pregenerationSize();
    if (frame?.height) {
      return { width: frame.width, height: frame.height };
    }

    const intrinsic = this.image().gravity;
    const hasIntrinsic = intrinsic && intrinsic.width > 0 && intrinsic.height > 0;

    if (width <= 0 && height <= 0 && hasIntrinsic) {
      const ratio = intrinsic.width / intrinsic.height;
      // A declared width wins over the global cap: it is the only case where we know
      // the container is wider than the cap allows.
      const declared = frame?.width;
      const target = declared ? Math.min(declared, intrinsic.width) : intrinsic.width;

      return this.capSize({ width: target, height: Math.round(target / ratio) }, !!declared);
    }

    // Nothing to go on: no configured size, and the media carries no dimensions either.
    // Video posters are in that case. Bounding the width alone keeps the file's ratio and
    // stops Cloudflare from serving the original: one poster measured 3424 Ko.
    if (width <= 0 && height <= 0) {
      const maxSize = environment.CDN?.maxPregenerationSize;

      return maxSize && maxSize > 0 ? { width: maxSize, height: 0 } : { width, height };
    }

    return this.capSize({ width, height }, false);
  }

  private capSize(size: Size, skipCap: boolean): Size {
    const maxSize = environment.CDN?.maxPregenerationSize;
    if (skipCap || !maxSize || maxSize <= 0 || size.width <= 0 || size.height <= 0) {
      return size;
    }

    const scale = Math.min(maxSize / size.width, maxSize / size.height, 1);

    return { width: Math.floor(size.width * scale), height: Math.floor(size.height * scale) };
  }

  /**
   * Sets up a ResizeObserver to detect size changes of the wrapper element,
   * then emits new dimensions through resizeSubject.
   */
  private initResizeObserver(): void {
    const elm = this.wrapperImageElement()?.nativeElement;
    if (!elm) return;
    this.resizeObserver = new ResizeObserver(() => {
      // Emit current offset dimensions
      this.resizeSubject.next({
        width: elm.offsetWidth,
        height: elm.offsetHeight,
      });
    });

    // Start observing the wrapper element
    this.resizeObserver.observe(elm);
  }

  /**
   * Updates width/height signals based on container size,
   * current breakpoints (mobile/desktop), and the aspect ratio setting.
   */
  private updateDimensions(containerWidth: number, containerHeight: number): void {
    // Retrieve final dimensions according to configuration and container size
    const { width, height } = this.getSizes(containerWidth, containerHeight);
    this.width.set(width);
    this.height.set(height);

    this.setFullWidthMode();
  }

  /**
   * Determine if the component should apply "fill" mode
   * by checking the provided sizeConfig and the keepAspectRatio setting.
   */
  private setFullWidthMode(): void {
    const sizeConfig = this.size();
    const isMobile = this.breakpoints() === 'mobile';
    this.isFullWidth.set(
      isMobile ? sizeConfig?.mw == null && !this.keepAspectRatio() : sizeConfig?.dw == null && !this.keepAspectRatio()
    );
  }

  /**
   * Checks whether the image should be updated based on valid dimensions
   * and a link that differs from the existing one.
   */
  private shouldUpdateImage(newCdnLink: string): boolean {
    const images = this.imagesArray();
    const firstImageCDNLink = images?.[0]?.cdnLink ?? null;
    const hasValidSize = this.width() > 0 && this.height() > 0;
    const isDifferentLink = newCdnLink !== firstImageCDNLink;

    return hasValidSize && isDifferentLink && !this.isRenderedSizeEnough();
  }

  /**
   * Replays the server pass on the client when hydration found a file already on screen.
   *
   * The template only keeps an <img> while `imagesArray` holds one, and hydration restores
   * the DOM but not the signals. Rebuilding the very same entry keeps the image in place at
   * no cost: the URL is identical, so the browser reuses what it has just downloaded.
   */
  private adoptServerRenderedImage(): void {
    if (!this.wrapperImageElement()?.nativeElement.querySelector('img')) {
      return;
    }

    this.renderPregeneratedImage();
  }

  /**
   * Whether the file already on screen covers the container, which is what
   * object-cover needs: it upscales by the larger of the two ratios.
   *
   * Skipping the request in that case removes the second download the server pass
   * had already made unnecessary.
   */
  private isRenderedSizeEnough(): boolean {
    const rendered = this.renderedSize();
    const needed = this.getRequestSize();
    if (!rendered || rendered.width <= 0 || rendered.height <= 0 || needed.width <= 0 || needed.height <= 0) {
      return false;
    }

    // Covering the container in pixels is not enough. A file whose ratio differs from the frame
    // gets cropped a second time by object-cover, which crops from the centre and so drops the
    // gravity the CMS set on the media (measured: the homepage hero, 1920x1536 in a 390x744 frame).
    const neededRatio = needed.width / needed.height;
    const fitsTheFrame = Math.abs(rendered.width / rendered.height - neededRatio) <= neededRatio * MAX_FRAME_RATIO_GAP;

    return fitsTheFrame && rendered.width >= needed.width && rendered.height >= needed.height;
  }

  /**
   * Size asked of the CDN for the container currently measured, in device pixels.
   *
   * The container is measured in CSS pixels, so a dense screen needs that many
   * times its ratio, otherwise the browser stretches the file it receives.
   */
  private getRequestSize(): Size {
    const density = this.isServer ? 1 : Math.min(globalThis.devicePixelRatio || 1, MAX_PIXEL_DENSITY);

    return {
      width: Math.round(this.width() * density),
      height: Math.round(this.height() * density),
    };
  }

  /**
   * Calculates the width/height based on container dimensions,
   * the current breakpoint (mobile/desktop), and the aspect ratio setting.
   */
  private getSizes(containerWidth: number, containerHeight: number): Size {
    const isMobileBreakpoint = this.breakpoints() === 'mobile';
    const shouldMaintainAspectRatio = this.keepAspectRatio();
    const configuredSize = this.size() ?? {};

    // If aspect ratio is preserved, compute dimensions accordingly
    if (shouldMaintainAspectRatio) {
      return this.getWidthAspectRatio(containerWidth, containerHeight);
    }

    // Otherwise, use explicit mobile/desktop dimensions or fallback to container sizes
    const width = isMobileBreakpoint ? (configuredSize.mw ?? containerWidth) : (configuredSize.dw ?? containerWidth);
    const height = isMobileBreakpoint ? (configuredSize.mh ?? containerHeight) : (configuredSize.dh ?? containerHeight);

    return { width, height };
  }

  /**
   * Respects the original image aspect ratio, applies optional max-size constraints,
   * and returns computed width/height based on the container's width.
   */
  private getWidthAspectRatio(containerWidth: number, containerHeight: number): Size {
    const isMobile = this.breakpoints() === 'mobile';
    const sizeConfig = this.size() ?? {};

    if (!sizeConfig.mw && sizeConfig.mh) {
      delete sizeConfig.mh;
    }
    if (!sizeConfig.dw && sizeConfig.dh) {
      delete sizeConfig.dh;
    }

    const configuredWidth = isMobile ? sizeConfig.mw : sizeConfig.dw;
    const configuredHeight = isMobile ? sizeConfig.mh : sizeConfig.dh;

    const intrinsic = this.image().gravity;
    if (!intrinsic?.width || !intrinsic.height) {
      // The media carries no dimensions, and video posters carry none. Dividing by them
      // yielded NaN, which travelled all the way to a URL without any size: 3424 Ko for a
      // single poster. Without a ratio to preserve, the container is all there is.
      return { width: configuredWidth ?? containerWidth, height: configuredHeight ?? containerHeight };
    }

    const { width: imageWidth, height: imageHeight } = intrinsic;
    const aspectRatio = imageWidth / imageHeight;

    // Start with the configured or container width, but cap it at the original image width to prevent upscaling.
    let finalWidth = Math.min(configuredWidth ?? containerWidth, imageWidth);
    let finalHeight = finalWidth / aspectRatio;

    // Determine the maximum allowed height, considering container, configured, and original image height.
    let maxHeight = containerHeight > 0 ? Math.min(containerHeight, imageHeight) : imageHeight;
    if (configuredHeight != null) {
      maxHeight = Math.min(maxHeight, configuredHeight);
    }

    // If calculated height exceeds the max, adjust both height and width to maintain aspect ratio.
    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = finalHeight * aspectRatio;
    }

    return {
      width: Math.round(finalWidth),
      height: Math.round(finalHeight),
    };
  }
}
